/**
 * Fondo de las pantallas sin sesión.
 *
 * El motivo no es decorativo por decorar: es el propio gesto de la marca. El
 * isotipo de Voxcom es una cinta de curvas paralelas, y el fondo lo repite a
 * escala de pantalla. Traer una estética prestada —hexágonos, mapamundis de
 * puntos, circuitos— habría dado una imagen genérica de «tecnología»; esto
 * dice de quién es el producto sin escribirlo dos veces.
 *
 * Por la misma razón el color sale del logo, azul a cian a verde, y no del
 * acento del panel.
 *
 * El mismo dibujo va detrás del panel, con la variante `panel`: más tenue y
 * quieto. Las dos diferencias tienen motivo. Ahí adentro las tarjetas son
 * opacas, así que el fondo solo asoma entre ellas, y a intensidad completa
 * competiría con lo único que importa en esa pantalla, que son los números.
 * Y la deriva se apaga: una pantalla de acceso puede respirar, pero algo que
 * se mueve despacio detrás de una tabla que alguien está leyendo es una
 * distracción permanente.
 *
 * Todo es un SVG dibujado por código: sin imágenes, sin pedidos de red, y
 * nítido en cualquier pantalla. Es lo primero que se ve del producto y tiene
 * que estar antes que nada.
 */

const ANCHO = 1600;
const ALTO = 900;

/** Una curva que cruza el ancho, con la misma forma de S del isotipo. */
function curva(y: number, amplitud: number): string {
  const a = amplitud;
  return [
    `M -40 ${y}`,
    `C ${ANCHO * 0.16} ${y - a}, ${ANCHO * 0.34} ${y - a}, ${ANCHO * 0.5} ${y}`,
    `C ${ANCHO * 0.66} ${y + a}, ${ANCHO * 0.84} ${y + a}, ${ANCHO + 40} ${y}`,
  ].join(' ');
}

/**
 * Una cinta de curvas paralelas.
 *
 * La amplitud se abre hacia el centro de la cinta y la opacidad cae hacia los
 * bordes: es lo que hace que se lea como un volumen y no como un peine.
 */
function cinta(centro: number, lineas: number, separacion: number, amplitud: number) {
  const salida = [];
  for (let i = 0; i < lineas; i++) {
    const t = i / (lineas - 1); // 0 … 1
    const desde = t - 0.5; // -0.5 … 0.5
    const y = centro + desde * separacion * lineas;
    // Coseno para que el centro tenga la mayor amplitud y los extremos la menor.
    const a = amplitud * (0.45 + 0.55 * Math.cos(desde * Math.PI));
    salida.push(
      <path
        key={`${centro}-${i}`}
        d={curva(y, a)}
        fill="none"
        stroke="url(#vox-marca)"
        strokeWidth={0.9}
        opacity={0.1 + 0.5 * Math.cos(desde * Math.PI) ** 2}
      />
    );
  }
  return salida;
}

/** Puntos sueltos. Pocos y sin patrón: sugieren nodos, no confeti. */
const NODOS = [
  { x: 250, y: 300, r: 2.5, o: 0.5 },
  { x: 520, y: 214, r: 1.6, o: 0.32 },
  { x: 700, y: 620, r: 3, o: 0.45 },
  { x: 980, y: 168, r: 2, o: 0.28 },
  { x: 1180, y: 430, r: 2.6, o: 0.4 },
  { x: 1350, y: 705, r: 1.8, o: 0.3 },
  { x: 1470, y: 250, r: 2.2, o: 0.35 },
  { x: 140, y: 640, r: 1.7, o: 0.3 },
  { x: 860, y: 800, r: 2.1, o: 0.26 },
];

export function FondoAcceso({ variante = 'acceso' }: { variante?: 'acceso' | 'panel' } = {}) {
  return (
    <div
      className={variante === 'panel' ? 'fondo-acceso fondo-panel' : 'fondo-acceso'}
      aria-hidden="true"
    >
      <svg
        viewBox={`0 0 ${ANCHO} ${ALTO}`}
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Los tres colores del isotipo, en el mismo orden. */}
          <linearGradient id="vox-marca" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#066ffa" />
            <stop offset="45%" stopColor="#13cce5" />
            <stop offset="100%" stopColor="#65cb03" />
          </linearGradient>

          <radialGradient id="vox-halo-azul" cx="0.18" cy="0.22" r="0.55">
            <stop offset="0%" stopColor="#066ffa" stopOpacity="0.16" />
            <stop offset="100%" stopColor="#066ffa" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="vox-halo-verde" cx="0.86" cy="0.82" r="0.5">
            <stop offset="0%" stopColor="#65cb03" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#65cb03" stopOpacity="0" />
          </radialGradient>

          {/*
            Viñeta. Es lo que mantiene la tarjeta como el centro de atención:
            sin ella, el fondo compite con el formulario, que es lo único que
            hay que hacer en esta pantalla.
          */}
          <radialGradient id="vox-vineta" cx="0.5" cy="0.5" r="0.75">
            <stop offset="35%" style={{ stopColor: 'var(--fondo)' }} stopOpacity="0" />
            <stop offset="100%" style={{ stopColor: 'var(--fondo)' }} stopOpacity="0.88" />
          </radialGradient>
        </defs>

        <rect width={ANCHO} height={ALTO} style={{ fill: 'var(--fondo)' }} />
        <rect width={ANCHO} height={ALTO} fill="url(#vox-halo-azul)" />
        <rect width={ANCHO} height={ALTO} fill="url(#vox-halo-verde)" />

        <g className="fondo-cintas">
          {cinta(560, 16, 9, 130)}
          {cinta(660, 10, 11, 90)}
        </g>

        {NODOS.map((n) => (
          <circle
            key={`${n.x}-${n.y}`}
            cx={n.x}
            cy={n.y}
            r={n.r}
            fill="url(#vox-marca)"
            opacity={n.o}
          />
        ))}

        <rect width={ANCHO} height={ALTO} fill="url(#vox-vineta)" />
      </svg>
    </div>
  );
}
